/**
 * The app orchestrator: wires the lobby, scene, board, HUD, interaction and animation layers into one
 * running local-hotseat game. [P6-place-ux] [P6-meeple-ux] [P6-anim] [P6-polish]
 *
 * "All seats local, no socket" is the whole of this build's dev/play mode — see the lane brief's hard
 * rule that this must work standalone against the engine, and that it is genuinely useful as hotseat
 * play in its own right, not just a fallback.
 */

import type { GameEvent, GameState } from '@carc/engine';
import { SEAT_COLOURS, type RoomOptions } from '@carc/protocol';
import { Scene } from './scene.js';
import { BoardView } from './board-view.js';
import { LocalSession, type SeatSetup } from './session.js';
import { RemoteSession } from './remote-session.js';
import { OnlineLobby } from './online-lobby.js';

/** Either session works here — they present the same surface. See remote-session.ts. */
type AnySession = LocalSession | RemoteSession;
import { Hud, seatColourHex } from './hud.js';
import { Lobby, type StartConfig } from './lobby.js';
import { MiniMap } from './minimap.js';
import { PlacementController } from './placement.js';
import { MeeplePicker } from './meeple-picker.js';
import { AnimationDirector } from './anim-director.js';
import { Toaster } from './toast.js';
import { SoundBoard } from './audio.js';
import { dominantKind } from './geometry.js';
import { HelpPanel, Tutorial } from './help.js';
import { newGameId, saveGame, type SavedGame } from './saved-games.js';
import { TurnTimer } from './turn-timer.js';

function seatEventName(seats: readonly SeatSetup[], seat: number): string {
  return seats[seat]?.name ?? `Seat ${seat + 1}`;
}

function describeEvent(event: GameEvent, seats: readonly SeatSetup[]): string | null {
  switch (event.type) {
    case 'tile_placed':
      return `${seatEventName(seats, event.seat)} placed tile ${event.defId} at (${event.pos.x}, ${event.pos.y}).`;
    case 'tile_discarded':
      return `Tile ${event.defId} could not be placed anywhere and was discarded.`;
    case 'meeple_placed':
      return `${seatEventName(seats, event.meeple.seat)} placed a meeple.`;
    case 'meeple_skipped':
      return `${seatEventName(seats, event.seat)} skipped placing a meeple.`;
    case 'feature_scored':
      if (event.points <= 0 || event.seats.length === 0) return null;
      return `${event.seats.map((s) => seatEventName(seats, s)).join(' & ')} scored ${event.points} pts (${event.kind}${event.completed ? ', completed' : ''}).`;
    case 'game_ended':
      return `Game over — final scores: ${event.finalScores.join(', ')}.`;
    default:
      return null;
  }
}

export class App {
  private readonly scene: Scene;
  private readonly toaster: Toaster;
  private readonly sounds = new SoundBoard();
  private readonly lobby: Lobby;
  private readonly onlineLobby: OnlineLobby;
  private readonly helpPanel: HelpPanel;
  private readonly tutorial = new Tutorial();
  /** Armed for the first turn of a game only — see the lane brief's first-run tutorial requirement. */
  private tutorialArmed = true;

  private session: AnySession | null = null;
  private board: BoardView | null = null;
  private hud: Hud | null = null;
  private minimap: MiniMap | null = null;
  private placement: PlacementController | null = null;
  private meeplePicker: MeeplePicker | null = null;
  private director: AnimationDirector | null = null;
  private lastAnnouncedSeat = -1;
  /** Identity of the local game being persisted, or null for online play (the server persists that). */
  private saveSlot: { id: string; name: string } | null = null;
  /** The options the local game was actually created with, so a save records them rather than guesses. */
  private sessionOptions: RoomOptions | null = null;
  private readonly passOverlay: HTMLElement;
  private passOverlayPending = false;
  /** The per-turn clock, rebuilt per session because its length is a per-game option. [P6-timer] */
  private turnTimer: TurnTimer | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly hudEl: HTMLElement,
    lobbyEl: HTMLElement,
    toastsEl: HTMLElement,
  ) {
    this.scene = new Scene(canvas);
    this.toaster = new Toaster(toastsEl);
    // A frame error no longer kills the loop (see scene.ts), but it must not be invisible either —
    // the player deserves to know something broke rather than wondering why the board went odd.
    this.scene.onTickError = (message) => {
      this.toaster.error(`Something glitched while drawing the board (${message}). The game is still playable — please tell Tony what you were doing.`);
    };
    this.helpPanel = new HelpPanel(() => this.replayTutorial());
    this.lobby = new Lobby(
      lobbyEl,
      (config) => this.startGame(config),
      (saved) => this.resumeGame(saved),
      () => {
        this.lobby.hide();
        this.onlineLobby.show();
      },
      () => this.helpPanel.open(),
    );

    this.onlineLobby = new OnlineLobby(
      (session) => this.startOnline(session),
      () => {
        this.onlineLobby.hide();
        this.lobby.show();
      },
    );
    document.body.appendChild(this.onlineLobby.element);
    this.onlineLobby.hide();

    // A shared invite link goes straight to the online lobby.
    if (new URLSearchParams(location.search).has('room')) {
      queueMicrotask(() => {
        this.lobby.hide();
        this.onlineLobby.show();
      });
    }

    this.passOverlay = document.createElement('div');
    this.passOverlay.className = 'pass-device-overlay hidden';
    this.passOverlay.innerHTML = `
      <div class="big"></div>
      <div class="hint">Tap or click anywhere to continue</div>
    `;
    document.body.appendChild(this.passOverlay);

    this.hudEl.classList.add('hidden');
    this.lobby.show();
  }

  private startGame(config: StartConfig): void {
    const seats: SeatSetup[] = config.seatNames.map((name, i) => {
      const pick = config.seatColours[i] ?? i;
      return {
        name,
        colour: seatColourHex(SEAT_COLOURS[pick % SEAT_COLOURS.length]?.id ?? 'slate'),
      };
    });

    const options: RoomOptions = {
      stackCount: config.stackCount,
      turnSeconds: config.turnSeconds,
      packs: ['base'],
    };
    this.session = new LocalSession({
      seed: (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0,
      seats,
      options,
    });
    this.sessionOptions = options;
    this.saveSlot = { id: newGameId(), name: config.gameName?.trim() || 'Untitled game' };
    this.beginSession(seats);
    this.persist();
  }

  /**
   * Bring a saved local game back.
   *
   * The saved log is replayed through the same pure engine that produced it, so the board, the
   * scores, the meeples and whose turn it is all come back exactly — nothing about the reconstruction
   * is approximate. If the log will not replay (an engine change since it was written, a truncated
   * save), `LocalSession` keeps the fresh game and says so, and the player is told rather than handed
   * a blank board that looks like theirs.
   */
  resumeGame(saved: SavedGame): void {
    const options: RoomOptions = {
      stackCount: saved.options.stackCount,
      turnSeconds: saved.options.turnSeconds,
      packs: [...saved.options.packs],
    };
    const session = new LocalSession({
      seed: saved.seed,
      seats: saved.seats,
      options,
      replay: saved.actions,
    });
    this.session = session;
    this.sessionOptions = options;
    this.saveSlot = { id: saved.id, name: saved.name };
    this.beginSession(saved.seats);
    // The whole history, regenerated. The log is built from live events and the replay ran before any
    // listener existed, so a resumed game used to open on a blank panel — which reads as "the record of
    // this game is gone", and would compound on a second resume. The engine is deterministic, so the
    // events it produced the first time are exactly the events it produces again; they are written
    // straight to the log rather than animated, because nobody wants to watch fifty moves replay.
    for (const event of session.replayedEvents) {
      const text = describeEvent(event, saved.seats);
      if (text) this.hud?.pushLog(text, false);
    }
    this.hud?.pushLog(
      `Resumed "${saved.name}" — ${session.state.board.size} tiles already on the board.`,
      true,
    );
    if (session.replayFailed) {
      this.toaster.error(
        `"${saved.name}" could not be replayed, so this is a new game. The save has been kept — please tell Tony.`,
      );
    }
  }

  /**
   * Write the local game down after every committed action.
   *
   * Cheap by design: only `(seed, options, actions[])` plus a little metadata for the lobby list.
   * Never the `GameState` — it holds Maps and the union-find forest, would not survive JSON, and
   * would become a second source of truth able to disagree with the log.
   */
  private persist(): void {
    if (!this.saveSlot || !this.session || this.session instanceof RemoteSession) return;
    const state = this.session.state;
    saveGame({
      id: this.saveSlot.id,
      name: this.saveSlot.name,
      seed: state.seed,
      seats: this.session.seats,
      options: {
        stackCount: state.stacks.length,
        // Read back from the session, not hardcoded. Writing 0 here meant every save silently forgot
        // its turn timer, so resuming a timed game handed you an untimed one.
        turnSeconds: this.sessionOptions?.turnSeconds ?? 0,
        packs: this.sessionOptions?.packs ?? ['base'],
      },
      actions: [...this.session.actions],
      savedAt: Date.now(),
      tilesPlaced: state.board.size,
      scores: [...state.scores],
      finished: state.phase === 'game_over',
    });
  }

  /**
   * Hand over to a session that is already connected and already has a game running. The scene setup
   * below is identical for local and online play — only where the state comes from differs.
   */
  startOnline(session: RemoteSession): void {
    this.session = session;
    this.saveSlot = null; // the server is the record for online rooms
    this.sessionOptions = null;
    this.beginSession(session.seats);
  }

  /**
   * Tear down everything owned by the previous session.
   *
   * Every one of these attaches itself to something that outlives it — the scene graph, the canvas,
   * the scene clock, `document.body`. Building a second set without dropping the first leaves the old
   * board's tiles still drawn beneath the new ones AND a second `PlacementController` tracking the
   * same pointer, which is the "duplicate tile" that kept coming back. Two entry points can reach
   * `beginSession` (local Start, and the online lobby), and the local Start button was a double-tap
   * away from calling it twice.
   */
  private disposeSessionViews(): void {
    this.turnTimer?.stop();
    this.placement?.dispose();
    this.meeplePicker?.dispose();
    this.director?.dispose();
    this.board?.dispose();
    this.minimap?.element.remove();
    this.hudEl.replaceChildren();
    this.placement = null;
    this.meeplePicker = null;
    this.director = null;
    this.board = null;
    this.minimap = null;
    this.hud = null;
  }

  private beginSession(seats: readonly SeatSetup[]): void {
    if (!this.session) return;
    this.disposeSessionViews();

    this.lobby.hide();
    this.hudEl.classList.remove('hidden');

    this.board = new BoardView(this.scene, this.session.tiles);
    // The start tile is placed synchronously by createGame(), before any event exists to animate —
    // render it directly rather than waiting for a 'tile_placed' event that will never come.
    for (const placed of this.session.state.board.values()) this.board.addTile(placed);
    // And every meeple ALREADY standing, for exactly the same reason. A meeple normally reaches the
    // board only through a `meeple_placed` event, and a resumed or joined-in-progress game replays its
    // whole log before any listener exists — so the pieces were spent from the supply, counted by the
    // scoreboard, and simply never drawn. Tony resumed a 51-tile game and found an empty board. ([D-36])
    for (const meeple of this.session.state.meeples) {
      this.board.addMeeple(meeple, this.session.seats[meeple.seat]?.colour ?? '#999');
    }
    // Local games have no server room, so they get no code and no share link ([D-32]).
    const code = this.session instanceof RemoteSession ? this.session.roomCode : null;
    this.hud = new Hud(this.hudEl, this.sounds, code, {
      onToggleSound: () => {
        const muted = this.sounds.toggle();
        this.hud?.setSoundMuted(muted);
      },
      onOpenHelp: () => this.helpPanel.open(),
      onRotate: () => this.placement?.rotate(),
      onSkipMeeple: () => this.meeplePicker?.skip(),
    });
    this.tutorialArmed = true;
    this.minimap = new MiniMap(this.scene, (world) => this.scene.panTo(world.x, world.z));
    this.hudEl.appendChild(this.minimap.element);
    this.scene.onTick(() => this.minimap?.tick());

    this.director = new AnimationDirector(this.board, this.scene, (seat) => this.session?.seats[seat]?.colour ?? '#999', this.sounds);

    this.placement = new PlacementController(
      this.scene,
      this.board,
      this.session,
      this.toaster,
      this.sounds,
      () => this.afterAction(),
      (rotation) => this.hud?.setPreviewRotation(rotation),
    );

    this.meeplePicker = new MeeplePicker(
      this.scene,
      this.board,
      this.session,
      this.sounds,
      () => this.afterAction(),
      (seat) => this.session?.seats[seat]?.name ?? `Player ${seat + 1}`,
    );

    // A local game enforces its own clock; an online one only ever DISPLAYS the server's, because the
    // server is the sole authority on whose turn ran out. A hybrid game is an online game — several
    // seats on this device, but still one server holding the clock — so it takes the display path too,
    // which is exactly what makes the timer safe to have in hotseat at last.
    this.turnTimer?.stop();
    const seconds = this.session instanceof RemoteSession ? 0 : (this.sessionOptions?.turnSeconds ?? 0);
    this.turnTimer = new TurnTimer(seconds, {
      onTick: (remaining) => this.hud?.setTurnClock(remaining),
      onExpire: () => this.autoPassLocalTurn(),
    });
    if (this.session instanceof RemoteSession) {
      this.session.onTurnDeadline = (msRemaining) => this.turnTimer?.showRemoteDeadline(msRemaining);
    }

    this.lastAnnouncedSeat = -1;
    this.session.onChange((state, events) => this.onSessionChange(state, events));

    this.hud.render(this.session.state, seats, this.session.tiles);
    this.refreshMinimap();
    this.advance(this.session.state);
  }

  private onSessionChange(state: GameState, events: readonly GameEvent[]): void {
    if (!this.session || !this.board || !this.hud || !this.director) return;

    this.persist();
    this.director.handle(events, state);
    this.hud.render(state, this.session.seats, this.session.tiles);

    for (const event of events) {
      const text = describeEvent(event, this.session.seats);
      if (text) this.hud.pushLog(text, true);
    }

    if (events.some((e) => e.type === 'tile_placed')) {
      this.refreshMinimap();
      this.scene.requestAutoFrame(this.board.boardBounds());
    }

    this.advance(state);
  }

  private refreshMinimap(): void {
    if (!this.session || !this.minimap) return;
    const entries = [...this.session.state.board.values()].map((placed) => {
      const def = this.session?.tiles.get(placed.defId);
      return { pos: placed.pos, kind: def ? dominantKind(def) : ('field' as const) };
    });
    this.minimap.setTiles(entries);
  }

  /**
   * Does THIS device control that seat?
   *
   * Locally, every seat is ours (that is what hotseat means). Remotely, only the seats this connection
   * has claimed — which may be several, and that is precisely what makes hybrid play work: two people
   * sharing this laptop hold two seats here, while someone else holds another somewhere else.
   */
  private controlsSeat(seat: number): boolean {
    if (!this.session) return false;
    if (!(this.session instanceof RemoteSession)) return true;
    return this.session.mySeats.includes(seat);
  }

  private advance(state: GameState): void {
    if (!this.session || !this.hud) return;
    // Cleared unconditionally on every entry — any lingering coach mark from a phase the player just
    // left (e.g. they placed the tile directly instead of clicking the step-3 "Got it") gets dropped
    // here rather than surviving into a phase it no longer describes.
    this.tutorial.hide();
    // Exactly one controller belongs to the board at a time, and the PHASE decides which. Leaving this
    // to each branch's own activate() is what put a second tile on the board in online play: a remote
    // action returns the *unchanged* state, so the optimistic advance re-entered the placement branch
    // and built a fresh floating tile, and when the server's real state finally arrived the meeple
    // branch activated the picker without ever telling placement to let go. Stating the invariant here
    // means no future branch can forget it. ([D-35])
    if (state.phase !== 'awaiting_placement') this.placement?.deactivate();
    if (state.phase !== 'awaiting_meeple') this.meeplePicker?.deactivate();

    // Per turn, not per game: the clock restarts when the seat on it changes. Keyed on the seat alone
    // rather than seat+phase, so drawing and then placing is one turn and one clock — thinking hard
    // about a placement costs you that turn and nothing more.
    // `advance()` runs several times per turn, so the guard has to live here rather than only where the
    // overlay is raised — a later advance would otherwise restart the clock behind the curtain.
    if (state.phase === 'game_over' || this.passOverlayPending) this.turnTimer?.stop();
    else this.turnTimer?.turnBegan(`seat:${state.activeSeat}:${state.board.size}`);

    if (state.phase !== 'game_over' && !this.controlsSeat(state.activeSeat)) {
      // Somebody else's turn, on somebody else's device. Watch it happen — the events still animate.
      this.hud.clearActions();
      this.placement?.deactivate();
      this.meeplePicker?.deactivate();
      const who = this.session.seats[state.activeSeat]?.name ?? `Seat ${state.activeSeat + 1}`;
      this.hud.setTurnBanner(`Waiting for ${who}…`);
      return;
    }

    if (state.phase === 'game_over') {
      this.hud.clearActions();
      const finalScores = state.scores;
      const best = Math.max(...finalScores);
      const winners = finalScores.map((s, i) => ({ s, i })).filter((x) => x.s === best).map((x) => x.i);
      window.setTimeout(() => {
        this.lobby.showGameOver(
          finalScores,
          winners,
          this.session?.seats.map((s) => s.name) ?? [],
          () => window.location.reload(),
        );
      }, 900);
      return;
    }

    if (state.phase === 'awaiting_draw') {
      this.handleAwaitingDraw(state);
      return;
    }

    if (state.phase === 'awaiting_placement') {
      this.hud.showPlacementControls();
      if (this.tutorialArmed && this.hud) {
        this.tutorial.enterPlacementPhase({
          tilePreview: this.hud.tilePreviewElement,
          rotateButton: this.hud.rotateButtonElement,
          board: this.scene.renderer.domElement,
        });
      }
      this.placement?.activate();
      return;
    }

    if (state.phase === 'awaiting_meeple') {
      this.hud.showMeepleControls();
      if (this.tutorialArmed && this.hud) {
        this.tutorial.enterMeeplePhase({ skipButton: this.hud.skipButtonElement });
        this.tutorialArmed = false; // the first-turn walkthrough is over after this
      }
      this.meeplePicker?.activate();
      return;
    }
  }

  /** "Replay tutorial" from the How-to-play panel: re-arm and, if a relevant phase is live, show it now. */
  private replayTutorial(): void {
    this.tutorial.reset();
    this.tutorialArmed = true;
    // Close the reference panel so the live coach mark isn't hidden behind it (and so the two focus
    // traps — the panel's and this session's next Tab-cycle over the coach mark's own buttons — don't
    // fight each other).
    this.helpPanel.close();
    if (this.session) this.advance(this.session.state);
  }

  private handleAwaitingDraw(state: GameState): void {
    this.hud?.clearActions();
    // "Pass the device" only makes sense when the device really is changing hands — i.e. the previous
    // seat was also one of ours. Online, when the turn arrives from someone else's device, there is
    // nothing to pass, so we just get on with it.
    // Note `>= 0`: on the very first turn nothing has been handed over yet, so prompting the player to
    // pass the device to themselves would be silly.
    const handedOver =
      state.activeSeat !== this.lastAnnouncedSeat &&
      this.lastAnnouncedSeat >= 0 &&
      this.controlsSeat(this.lastAnnouncedSeat);

    if (handedOver) {
      this.showPassOverlay(state.activeSeat, () => {
        this.lastAnnouncedSeat = state.activeSeat;
        this.doDraw(state);
      });
      return;
    }
    this.lastAnnouncedSeat = state.activeSeat;
    this.doDraw(state);
  }

  private doDraw(state: GameState): void {
    if (!this.session || !this.hud) return;
    const nonEmpty = state.stacks.map((s, i) => ({ len: s.length, i })).filter((x) => x.len > 0).map((x) => x.i);
    if (nonEmpty.length <= 1) {
      this.hud.clearActions();
      this.session.draw(nonEmpty[0]);
    } else {
      this.hud.showStackChoice(nonEmpty, (stack) => {
        this.hud?.clearActions();
        this.session?.draw(stack);
      });
    }
  }

  /**
   * A controller finished its move.
   *
   * Locally the state has already changed, so advancing now is what keeps play immediate. Remotely it
   * has NOT: `RemoteSession` dispatches to the server and hands back the state it still holds, so
   * advancing here would re-enter the phase the player has just left and rebuild the very tile they
   * placed. The authoritative state arrives moments later on the broadcast and drives `advance()` then.
   */
  /**
   * The local clock ran out. Do exactly what the server does to a stalled seat ([P6-timer]).
   *
   * Draw if a tile is owed, place it at the first legal spot, and never place a meeple — guessing which
   * feature somebody wanted is worse than taking nothing. If the tile fits nowhere the engine discards
   * it, which is the same thing that would happen on a real table.
   */
  private autoPassLocalTurn(): void {
    const session = this.session;
    if (!session || session instanceof RemoteSession) return;
    const seat = session.state.activeSeat;

    if (session.state.phase === 'awaiting_draw') {
      const stack = session.state.stacks.findIndex((s) => s.length > 0);
      session.draw(stack >= 0 ? stack : undefined);
    }
    if (session.state.phase === 'awaiting_placement') {
      const spot = session.legalPlacementsForCurrentTile()[0];
      if (spot) session.placeTile(spot.pos, spot.rotation);
    }
    if (session.state.phase === 'awaiting_meeple') session.skipMeeple();

    const who = session.seats[seat]?.name ?? `Seat ${seat + 1}`;
    this.toaster.error(`${who} ran out of time — the tile was placed for them and no meeple was taken.`);
    this.hud?.pushLog(`${who} ran out of time.`, true);
    this.advance(session.state);
  }

  private afterAction(): void {
    if (!this.session || this.session instanceof RemoteSession) return;
    this.advance(this.session.state);
  }

  private showPassOverlay(seat: number, onDismiss: () => void): void {
    if (!this.session) return;
    // Idempotent for the same reason placement.ts/meeple-picker.ts's activate() is: `advance()`
    // legitimately runs twice for the same resulting state (the session's own onChange listener, plus
    // the controller's post-action callback). Without this guard, a second `handleAwaitingDraw` call
    // attached a SECOND 'pointerdown' listener here, so one tap fired `doDraw` twice — the second call's
    // unconditional `clearActions()` immediately wiped the placement controls the first call had just
    // set (visible as the rotate button and instruction vanishing right after the device-pass overlay).
    if (this.passOverlayPending) return;
    this.passOverlayPending = true;
    // Nobody is on the clock while the device is still in transit. Without this a hotseat timer counts
    // down behind the handover curtain and can auto-pass a player who has not yet been handed the
    // board — punishing them for a turn they never saw. The clock restarts from `advance()` once the
    // overlay is dismissed and play actually begins. [P6-timer]
    this.turnTimer?.stop();

    const name = seatEventName(this.session.seats, seat);
    const big = this.passOverlay.querySelector('.big') as HTMLElement;
    big.textContent = `Pass the device to ${name}`;
    big.style.color = this.session.seats[seat]?.colour ?? '#fff';
    this.passOverlay.classList.remove('hidden');
    this.sounds.play('turn');

    const handler = (): void => {
      this.passOverlayPending = false;
      this.passOverlay.classList.add('hidden');
      this.passOverlay.removeEventListener('pointerdown', handler);
      onDismiss();
    };
    this.passOverlay.addEventListener('pointerdown', handler);
  }
}
